import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Bell, Clock } from 'lucide-react';
import { notificacionService, Notificacion } from '../../services/notificacionService';

interface CampanaNotificacionesProps {
  /**
   * Cromo del header donde se monta: el botón móvil es redondo y el de
   * escritorio lleva `hover:bg-accent`. La biñeta es igual en los dos.
   */
  variante: 'movil' | 'escritorio';
}

/**
 * Campana de notificaciones del header, con su biñeta.
 *
 * POR QUÉ UNA BIÑETA Y NO NAVEGAR A /notificaciones
 * ---------------------------------------------------
 * Antes, tocar la campana navegaba a la página completa y el punto rojo
 * estaba SIEMPRE encendido, aunque no hubiera nada sin leer: entrenaba
 * al usuario a ignorarlo. Ahora el punto solo existe cuando hay
 * notificaciones sin leer, y tocar la campana despliega una biñeta
 * anclada a ella —no cubre la pantalla— para leer y marcar sin cambiar
 * de pantalla. "Ver todas" lleva a la página completa.
 *
 * La biñeta abre y cierra HACIA la campana: `origin-top-right` (su
 * esquina superior derecha es la esquina de la campana) con escala y
 * opacidad en transición. Está siempre montada con `invisible` al
 * cerrar para que el cierre sea un repliegue y no una desaparición;
 * `visibility: hidden` la saca del árbol de accesibilidad y del
 * hit-testing mientras está cerrada.
 */
export default function CampanaNotificaciones({ variante }: CampanaNotificacionesProps) {
  const navigate = useNavigate();
  const [notificaciones, setNotificaciones] = useState<Notificacion[]>([]);
  const [abierta, setAbierta] = useState(false);
  const contenedorRef = useRef<HTMLDivElement>(null);

  const cargar = useCallback(async () => {
    try {
      setNotificaciones(await notificacionService.listar());
    } catch (error) {
      // Ante error no se enciende el punto: mejor no afirmar que hay
      // notificaciones cuando no se pudieron leer.
      console.error('Error al cargar notificaciones:', error);
    }
  }, []);

  // Lectura inicial: el punto rojo tiene que ser correcto antes de que el
  // usuario abra la biñeta.
  useEffect(() => {
    cargar();
  }, [cargar]);

  // Al abrir se vuelve a pedir: la biñeta siempre muestra lo último del
  // servidor (la página de notificaciones pudo haberlas marcado leídas
  // desde la última lectura).
  useEffect(() => {
    if (abierta) cargar();
  }, [abierta, cargar]);

  // Cerrar al tocar fuera o con Escape.
  useEffect(() => {
    if (!abierta) return;

    const cerrarFuera = (evento: PointerEvent) => {
      if (contenedorRef.current && !contenedorRef.current.contains(evento.target as Node)) {
        setAbierta(false);
      }
    };
    const cerrarEscape = (evento: KeyboardEvent) => {
      if (evento.key === 'Escape') setAbierta(false);
    };

    document.addEventListener('pointerdown', cerrarFuera);
    document.addEventListener('keydown', cerrarEscape);
    return () => {
      document.removeEventListener('pointerdown', cerrarFuera);
      document.removeEventListener('keydown', cerrarEscape);
    };
  }, [abierta]);

  const sinLeer = notificaciones.filter((n) => !n.leida).length;

  const marcarLeida = async (id: number) => {
    try {
      await notificacionService.marcarLeida(id);
      setNotificaciones((prev) => prev.map((n) => (n.id === id ? { ...n, leida: true } : n)));
    } catch (error) {
      console.error('Error al marcar notificación:', error);
    }
  };

  const clasesBoton =
    variante === 'movil'
      ? 'relative grid h-12 w-12 place-items-center rounded-full text-v-on-surface transition-colors active:bg-black/5'
      : 'relative grid h-12 w-12 place-items-center rounded-lg hover:bg-accent';

  return (
    <div ref={contenedorRef} className="relative">
      <button
        type="button"
        onClick={() => setAbierta((v) => !v)}
        aria-label="Notificaciones"
        aria-expanded={abierta}
        aria-haspopup="true"
        className={clasesBoton}
      >
        <Bell className="h-5 w-5" />
        {sinLeer > 0 && (
          <span
            aria-hidden
            className="absolute right-3.5 top-3.5 h-2 w-2 rounded-full bg-v-error"
          />
        )}
      </button>

      <section
        aria-label="Notificaciones"
        className={`absolute right-0 top-full z-40 mt-2 w-80 max-w-[calc(100vw-1.5rem)] origin-top-right rounded-xl border border-v-outline bg-card shadow-xl transition-all duration-200 ${
          abierta
            ? 'visible translate-y-0 scale-100 opacity-100'
            : 'invisible -translate-y-1 scale-95 opacity-0'
        }`}
      >
        <div className="flex items-center justify-between gap-2 border-b border-v-outline px-4 py-3">
          <h2 className="text-sm font-bold text-v-on-surface">Notificaciones</h2>
          {sinLeer > 0 && (
            <span className="rounded-full bg-v-error-container px-2 py-0.5 text-xs font-semibold text-v-error">
              {sinLeer} sin leer
            </span>
          )}
        </div>

        <div className="scroll-nativo max-h-[60dvh] overflow-y-auto">
          {notificaciones.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <Bell className="mx-auto mb-2 h-8 w-8 text-v-on-surface-variant" />
              <p className="text-sm text-v-on-surface-variant">No tienes notificaciones</p>
            </div>
          ) : (
            <ul>
              {notificaciones.slice(0, 10).map((n) => (
                <li key={n.id}>
                  <button
                    type="button"
                    onClick={() => marcarLeida(n.id)}
                    className={`flex w-full items-start gap-3 px-4 py-3 text-left transition-colors active:bg-black/5 ${
                      n.leida ? '' : 'bg-primary/5'
                    }`}
                  >
                    <span
                      aria-hidden
                      className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                        n.leida ? 'bg-v-outline' : 'bg-v-primary'
                      }`}
                    />
                    <span className="min-w-0 flex-1">
                      <span
                        className={`block text-sm ${
                          n.leida
                            ? 'text-v-on-surface-variant'
                            : 'font-semibold text-v-on-surface'
                        }`}
                      >
                        {n.titulo}
                      </span>
                      <span className="mt-0.5 flex items-center gap-1 text-xs text-v-on-surface-variant">
                        <Clock className="h-3 w-3" />
                        {tiempoRelativo(n.createdAt)}
                      </span>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        {notificaciones.length > 0 && (
          <button
            type="button"
            onClick={() => {
              setAbierta(false);
              navigate('/notificaciones');
            }}
            className="flex min-h-12 w-full items-center justify-center border-t border-v-outline px-4 py-3 text-center text-sm font-semibold text-v-primary transition-colors active:bg-black/5"
          >
            Ver todas las notificaciones
          </button>
        )}
      </section>
    </div>
  );
}

/** "Hace 5 min", igual que la página de notificaciones. */
function tiempoRelativo(iso: string) {
  const diffMin = Math.floor((Date.now() - new Date(iso).getTime()) / 60000);
  if (diffMin < 1) return 'Ahora';
  if (diffMin < 60) return `Hace ${diffMin} min`;
  const horas = Math.floor(diffMin / 60);
  if (horas < 24) return `Hace ${horas} h`;
  return new Date(iso).toLocaleDateString('es-CO', { day: 'numeric', month: 'short' });
}
